import { onScreen } from './visible';
/**
 * 조작 중에 뜨는 대화상자와 로딩 가림막을 다룬다.
 *
 * 이 앱의 대화상자는 두 종류다.
 *
 * | | 취소 버튼 | 무엇인가 |
 * | --- | --- | --- |
 * | Warning·Success·Fail | 없음 | 알린다 |
 * | Confirm·Delete | 있음 | **묻는다** |
 *
 * **알리는 것만 닫는다.** 닫아도 아무 일이 일어나지 않는다.
 *
 * **묻는 것은 손대지 않는다.** 대신 던진다. 에이전트가 "확인"을 누르면 삭제나 저장을
 * 사람 대신 승낙하는 것이 되고, 드라이버를 읽기 전용으로 만든 뜻이 사라진다.
 * "취소"를 눌러 넘어가는 쪽도 안 된다 — 사용자가 하려던 일이 조용히 취소된다.
 */

const POLL_MS = 25;
const DIALOG = '.swal2-container';
const CANCEL = '.swal2-cancel';
const CONFIRM = '.swal2-confirm';
const LOADING = '#loading-screen';
const NOTICE_POLL_MS = 25;
const FOLLOWUP_NOTICE_MS = 250;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => { setTimeout(resolve, ms); });
}

// 「지금 화면에 있는가」는 `visible.ts` 하나가 정한다.
const visible = onScreen;

/**
 * 사람이 읽을 수 있는 알림 문구.
 *
 * 대화상자 전체 글자를 긁으면 아이콘 문자와 버튼 라벨이 섞여
 * `i기능 없음×견적없는 자재청구는...CancelNo확인` 같은 것이 사용자에게 간다.
 * 제목과 본문만 남긴다. 그 구조가 없는 대화상자만 전체 글자로 돌아간다.
 */
function textOf(dialog: Element): string {
  const clean = (value: string | null | undefined) => (value ?? '').replace(/\s+/g, ' ').trim();
  const title = clean(dialog.querySelector('.swal2-title')?.textContent);
  const body = clean(dialog.querySelector('.swal2-html-container')?.textContent);
  const spoken = [title, body].filter(Boolean).join(': ');
  return (spoken || clean(dialog.textContent)).slice(0, 200);
}

/** 떠 있는 swal 창. 없으면 `null`. */
function shownDialog(): Element | null {
  const dialog = document.querySelector(DIALOG);
  return visible(dialog) ? dialog : null;
}

/** 앱이 사람에게 묻고 있는가. 물으면 그 문구를 준다. */
export function questionAsked(): string | null {
  const dialog = shownDialog();
  if (!dialog) return null;
  return visible(dialog.querySelector(CANCEL)) ? textOf(dialog) : null;
}

/**
 * 확인·삭제처럼 선택을 요구하는 팝업은 문구를 보존한 뒤 안전한 취소로 즉시 닫는다.
 * 확인을 누르면 저장·삭제·화면 이탈이 실행될 수 있으므로 자동 승인하지 않는다.
 */
export function cancelQuestion(): string | null {
  const dialog = shownDialog();
  if (!dialog) return null;
  const cancel = dialog.querySelector(CANCEL);
  if (!visible(cancel)) return null;
  const message = textOf(dialog);
  cancel.click();
  return message;
}

/**
 * **물음에 그 이름의 단추로 답한다.** 버튼을 누른 뒤 화면이 묻는 것(`Confirm.ask`)에 쓴다.
 *
 * 확인 자리(`.swal2-confirm`)가 늘 「예」는 아니다. 지로일괄발행은 `Confirm.ask(…, '아니오', '예')`
 * 로 물어 확인 자리에 「아니오」가 있고, 「예」가 취소 자리에서 일을 진행시킨다. 자리로 누르면
 * 일이 취소된다 — 글자로 고른다. 이름을 앞에서부터 찾아 처음 있는 단추를 누르고, 하나도
 * 없으면 누르지 않고 `null` 이다.
 *
 * 같은 까닭으로 **취소 자리가 안전하다고 믿으면 안 된다.** 그 화면에서 취소 자리를 누르면
 * 「예」라서 일이 진행된다. 거절하려면 `NO_LABELS` 로 글자를 찾는다.
 */
export function answerQuestion(labels: string[]): { message: string; pressed: string } | null {
  const dialog = shownDialog();
  if (!dialog || !visible(dialog.querySelector(CANCEL))) return null;
  const buttons = Array.from(dialog.querySelectorAll<HTMLElement>(`${CONFIRM}, ${CANCEL}`)).filter(visible);
  const squash = (value: string) => value.replace(/\s+/g, '');
  for (const label of labels) {
    const button = buttons.find((node) => squash(node.textContent ?? '') === squash(label));
    if (!button) continue;
    const message = textOf(dialog);
    button.click();
    return { message, pressed: label };
  }
  return null;
}

/** 「하겠다」로 읽히는 단추 글자. 고르라 한 글자가 없을 때 차례로 찾는다. */
export const YES_LABELS = ['예', '확인', '네'];
/** 「안 하겠다」로 읽히는 단추 글자. */
export const NO_LABELS = ['아니오', '아니요', '취소'];

/**
 * 같은 팝업을 **확인으로** 닫는다. Auto 모드에서만 부른다.
 *
 * 문구는 그대로 돌려준다 — 무엇을 승인했는지 답변에 적어야 사용자가 나중에 확인할 수
 * 있다. 승인은 사용자가 패널에서 Auto 를 켠 뜻이지, 여기서 정하는 것이 아니다.
 */
/**
 * **예를 눌러서는 안 되는 물음.**
 *
 * 화면이 저장 뒤에 「다음 화면으로 넘어가겠습니까」를 묻는 일이 있다. 예를 누르면 화면을
 * 떠나고 **남은 걸음이 죽는다** — 인사등록은 기본정보를 저장한 뒤 사용자 등록으로
 * 가겠느냐고 묻는데, 거기 따라가면 가족·자격증을 못 쓴다.
 *
 * 사람이 하는 일을 대신하는 것이 목표이므로 **일 자체는 거절하지 않는다.** 여기 담는 것은
 * 「지금 이 일을 그만두고 다른 데로 가겠는가」와, 저장 직전 화면이 띄우는 「선택」 대화
 * 중 표에 오른 것뿐이다.
 *
 * **저장 직전 「선택」 대화.** 여기 오른 것만 자동으로 답하고, 표에 없는 「선택」 대화는
 * 멈춰서 사람에게 그 문구를 보인다(스펙 §5).
 *
 * **바깥으로 나가는 것도 아니오다.** 고장접수는 저장 뒤 「알림전송이 없는 고장접수내역입니다.
 * 알림을 전송하시겠습니까?」를 묻고, 예면 기사에게 알림이 간다. 사람은 82번 중 69번 아니오였다
 * (2026-09-29~10-13 사용 기록, 14테넌트). 알림은 사람이 따로 시킬 때 보낸다.
 */
const DECLINE = [
  /페이지로 넘어가|화면으로 이동|이동하시겠습니까/,
  // 견적에만 좁게 건다. `불러오시겠습니까`만으로는 임시저장·이전작성 불러오기까지 걸려
  // 표에 없는 대화를 조용히 취소로 넘긴다 — §7 이 금하는 자리다.
  // 당직계획 근무자 창에서 팀장을 고르면 「선택한 사원이 팀장입니다팀장의 팀을 전부 넣으시겠습니까?」를
  // 묻는다. 예를 누르면 말하지 않은 팀원들이 그날 당직으로 들어간다 — 사람이 한 사람을 말했으면
  // 한 사람만 넣는다(2026-10-05, 앱 `PlanEnroll.onMessageEvent`).
  /팀장의 팀을 전부 넣/,
  /알림을 전송하시겠습니까/,
];

/**
 * **저장 직전에 예로 답할 물음.** Auto 일 때만 쓴다.
 *
 * 발주등록은 상세행에 현장을 넣으면 「OO현장으로 전부 설정하시겠습니까?」를 띄운다.
 * 사용자는 현장을 하나 말했고 그것을 모든 행에 넣었으니 사람도 예를 누른다. 표에 없으면
 * 지금처럼 멈춘다 — 저장 직전 경로가 이것을 아니오로 닫고 `앱이 확인을 요구한다` 로
 * 끝냈다(2026-09-25).
 */
const ACCEPT_BEFORE_SAVE = [
  /현장으로 전부 설정하시겠습니까/,
  // 공사등록은 저장 직전 「해당 현장으로 견적내용이 있습니다견적내용을 불러오시겠습니까?」를 묻는다.
  // 전에는 아니오였다(새 공사에 옛 견적을 붙이지 않는다고 봤다). 사람은 67번 중 66번 예였다 —
  // 공사는 그 현장 견적으로 만든다(사용 기록 9테넌트, 사용자 결정 2026-10-13). 견적에만 좁게 건다:
  // `불러오시겠습니까` 만으로는 임시저장·이전작성 불러오기까지 걸린다.
  /견적내용을 불러오/,
];

/** 저장 직전 떠 있는 그 물음에 예로 답해도 되나. */
export function acceptsBeforeSave(message: string): boolean {
  return ACCEPT_BEFORE_SAVE.some((one) => one.test(message));
}

/** 그 물음에 아니오로 답해야 하나. */
export function declines(message: string): boolean {
  return DECLINE.some((one) => one.test(message));
}

export function confirmQuestion(): string | null {
  const dialog = shownDialog();
  if (!dialog) return null;
  const confirm = dialog.querySelector(CONFIRM);
  if (!visible(confirm)) return null;
  const message = textOf(dialog);
  // 「다른 화면으로 가겠는가」는 아니오다. 예를 누르면 화면을 떠나 남은 걸음이 죽는다.
  if (declines(message)) {
    const cancel = dialog.querySelector(CANCEL);
    if (visible(cancel)) {
      cancel.click();
      return message;
    }
  }
  confirm.click();
  return message;
}

/**
 * **방금 답한 창이 사라지기를 기다린다.**
 *
 * swal 은 눌러도 즉시 안 닫힌다 — 실측 0.3초. 그동안 다시 물으면 `confirmQuestion` 이
 * **같은 창**을 또 읽고 이미 눌린 단추를 또 누른다. 확인 라운드 셋이 몇 밀리초 만에
 * 한 창에 다 타 버리고, 0.6초 뒤에 뜨는 **다음 창은 아무도 안 본다**.
 *
 * 매입등록이 그 자리다: 「프로젝트번호…」가 세 번 찍히고, 정작 저장을 막은
 * 「거래처를 확인하십시오」는 사용자에게 안 갔다(2026-09-16 실측).
 *
 * `dismissNotices` 는 처음부터 이렇게 한다. 확인 고리에만 이 규율이 없었다.
 */
export async function settleDialog(timeoutMs: number): Promise<void> {
  const dialog = document.querySelector(DIALOG);
  if (!visible(dialog)) return;
  const deadline = Date.now() + timeoutMs;
  while (visible(dialog) && Date.now() < deadline) {
    // eslint-disable-next-line no-await-in-loop
    await sleep(POLL_MS);
  }
}

/**
 * 알림만 닫는다. 닫은 것들의 문구를 돌려준다 — 답변에 "조회 중 이런 알림이 떴다" 로
 * 남길 수 있어야 한다. 조용히 닫으면 사용자가 못 본 것을 못 봤다는 사실조차 모른다.
 */
export async function dismissNotices(limit = 3, appearMs = 0): Promise<string[]> {
  const closed: string[] = [];
  for (let round = 0; round < limit; round += 1) {
    const deadline = Date.now() + (round === 0 ? appearMs : FOLLOWUP_NOTICE_MS);
    let dialog = document.querySelector(DIALOG);
    while (!visible(dialog) && Date.now() < deadline) {
      // 팝업이 렌더되는 즉시 닫는다. 고정 sleep 뒤 확인하면 사용자는 그만큼 팝업을 보게 된다.
      // eslint-disable-next-line no-await-in-loop
      await sleep(NOTICE_POLL_MS);
      dialog = document.querySelector(DIALOG);
    }
    if (!visible(dialog)) break;
    if (visible(dialog.querySelector(CANCEL))) break;
    const confirm = dialog.querySelector(CONFIRM);
    if (!visible(confirm)) break;
    closed.push(textOf(dialog));
    confirm.click();
    // 같은 팝업을 다시 누르지 않고, 닫힌 뒤 후속 알림만 짧게 기다린다.
    const closeDeadline = Date.now() + FOLLOWUP_NOTICE_MS;
    while (visible(dialog) && Date.now() < closeDeadline) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(NOTICE_POLL_MS);
    }
  }
  return closed;
}

/**
 * 로딩 가림막이 걷히기를 기다린다.
 *
 * 대화상자가 아니라 화면 전체를 덮는 판이다. 이것이 떠 있는 동안 누른 클릭은
 * 그 판이 먹는다 — 눌렀다고 생각하고 결과를 기다리다 시간만 보낸다.
 */
/**
 * **가림막이 실제로 덮고 있나.**
 *
 * 자리만 보면 안 된다. 이 앱의 `#loading-screen` 은 **늘 DOM 에 있고 `display:flex` 에
 * 화면 전체 크기(1280x720)** 이며, 숨길 때 `opacity: 0` 으로만 숨긴다. 그래서
 * 「자리를 차지하는가」로 물으면 **언제나 떠 있다**고 답하고, `waitForCurtain` 이
 * 시한(5초)을 꽉 채워 기다린다 — 조회·저장·신규·삭제 **모든 동작마다** 5초다.
 *
 * 실측(2026-09-20): 조회 단추를 누르면 opacity 가 +0.8초부터 올라 +1.5초에 1이 되고,
 * 일이 끝나면 +2.8초에 0으로 돌아간다. 평상시에는 0이다. 그러니 opacity 로 묻는다.
 *
 * 걸음 하나를 재면 `runAction('retrieve')` 가 5,288ms 였고 그중 5,000ms 가 이 기다림이다.
 */
export function curtained(): boolean {
  const curtain = document.querySelector(LOADING);
  if (!visible(curtain)) return false;
  const shown = Number(getComputedStyle(curtain).opacity);
  // 브라우저가 opacity 를 안 주면(시험 환경 등) 예전처럼 자리로만 판단한다.
  return !Number.isFinite(shown) || shown > 0.1;
}

export async function waitForCurtain(deadline: number): Promise<void> {
  while (curtained() && Date.now() < deadline) {
    // eslint-disable-next-line no-await-in-loop
    await sleep(POLL_MS);
  }
}

/**
 * **앱이 스스로 띄우는 알림 창** — 일과 상관없이 떠서 화면을 가린다. 담당자변경 같은 일하는 창은 여기 없다.
 * 제목(창 안 첫 글자)으로 가린다. 새 알림 창을 보면 여기에 더한다(사용자 2026-10-15: 「알림 팝업은 바로 껐으면」).
 */
export const INFO_MODALS = ['계약만료현장알림'];

/**
 * 떠 있는 알림 창을 닫는다. 앱의 창(`ModalView`)은 바깥 배경을 누르면 닫힌다 — 본문(`role=dialog`) 바로 앞이
 * 그 배경이다. 닫은 창의 제목을 돌려준다.
 */
export function closeInfoModals(): string[] {
  const closed: string[] = [];
  for (const dialog of Array.from(document.querySelectorAll<HTMLElement>('[role="dialog"]'))) {
    const text = (dialog.textContent ?? '').replace(/\s+/g, ' ').trim();
    const title = INFO_MODALS.find((one) => text.startsWith(one));
    if (!title) continue;
    const background = dialog.previousElementSibling as HTMLElement | null;
    if (!background) continue;
    background.click();
    closed.push(title);
  }
  return closed;
}
